import {
  BodyText,
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@/components/ui";
import { formatPhoneNumber } from "@/lib/formatters";
import { useParams } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { CoverageAlerts } from "../CoverageAlerts";
import { useRxEligibility } from "../Eligibility/network/useRxEligibility";
import type { PrescriptionBenefitsButtonProps } from "./PrescriptionBenefitsButton";
import type {
  PrescriptionBenefitsResponse,
  RxBenefitsPatientSpecificPricingCoverage,
} from "./network/useRxPrescriptionBenefits";
import { useRxPrescriptionBenefits } from "./network/useRxPrescriptionBenefits";

interface PrescriptionBenefitsModalProps
  extends PrescriptionBenefitsButtonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ParamRowProps {
  label: string;
  value: string | number | null | undefined;
}

const ParamRow = ({ label, value }: ParamRowProps) => (
  <div className="flex justify-between text-sm py-1.5 border-b last:border-0">
    <span className="text-tertiary">{label}</span>
    <span
      className={
        value != null
          ? ""
          : "rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700"
      }
    >
      {value ?? "Missing"}
    </span>
  </div>
);

const FORMULARY_STATUS_LABELS: Record<string, string> = {
  U: "Unknown",
  "0": "Not Reimbursable",
  "1": "Non Formulary",
  "2": "On Formulary (Not Preferred)",
  "3": "Preferred Level 1",
  "4": "Preferred Level 2",
  "5": "Preferred Level 3",
};

const formatFormularyStatus = (status: string): string => {
  if (status in FORMULARY_STATUS_LABELS) return FORMULARY_STATUS_LABELS[status];
  const numeric = Number(status);
  if (!Number.isNaN(numeric) && numeric >= 3)
    return `Preferred Level ${numeric - 2}`;
  return status;
};

const PricingSection = ({
  coverages,
}: {
  coverages: RxBenefitsPatientSpecificPricingCoverage[] | null;
}) => {
  const pricing = coverages?.[0]?.pricingCoverage;
  if (!pricing) return null;

  return (
    <div className="flex flex-col gap-2">
      <CoverageAlerts alerts={pricing.coverageAlerts} />
      <div className="rounded-md border overflow-hidden px-3">
        <ParamRow
          label="Quantity"
          value={
            pricing.quantityPriced != null &&
            pricing.quantityPricedUnitOfMeasureType != null
              ? `${pricing.quantityPriced} ${pricing.quantityPricedUnitOfMeasureType}`
              : pricing.quantityPriced
          }
        />
        <ParamRow
          label="Days Supply"
          value={
            pricing.daysSupplyPriced ? `${pricing.daysSupplyPriced}d` : null
          }
        />
        {pricing.planPayAmount != null && (
          <ParamRow label="Plan Pay" value={`$${pricing.planPayAmount}`} />
        )}
        <ParamRow
          label="Est. Patient Pay"
          value={
            pricing.estimatedPatientPayAmount != null
              ? `$${pricing.estimatedPatientPayAmount}`
              : null
          }
        />
        {pricing.oopAppliedAmount != null && (
          <ParamRow
            label="OOP Applied"
            value={`$${pricing.oopAppliedAmount}`}
          />
        )}
        {pricing.oopRemainingAmount != null && (
          <ParamRow
            label="OOP Remaining"
            value={`$${pricing.oopRemainingAmount}`}
          />
        )}
        {pricing.deductibleAppliedAmount != null && (
          <ParamRow
            label="Deductible Applied"
            value={`$${pricing.deductibleAppliedAmount}`}
          />
        )}
        {pricing.deductibleRemainingAmount != null && (
          <ParamRow
            label="Deductible Remaining"
            value={`$${pricing.deductibleRemainingAmount}`}
          />
        )}
        {pricing.formularyStatus != null && (
          <ParamRow
            label="Formulary Status"
            value={formatFormularyStatus(pricing.formularyStatus)}
          />
        )}
        {pricing.priorAuthRequired != null && (
          <div className="flex justify-between text-sm py-1.5 border-b last:border-0">
            <span className="text-tertiary">Prior Auth Required</span>
            <span
              className={
                pricing.priorAuthRequired ? "text-error" : "text-success"
              }
            >
              {pricing.priorAuthRequired ? "Yes" : "No"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const BenefitsData = ({ data }: { data: PrescriptionBenefitsResponse }) => {
  const hasRealTimeData = !!data.realTimeFormularyRequestStatus;
  const alternatives = data.alternatives?.filter(
    (a) => a.patientSpecificPricingCoverages?.length,
  );

  return (
    <div className="flex flex-col gap-4">
      {!hasRealTimeData && (
        <Callout
          variant="warning"
          title="Real-time pricing unavailable"
          description="Live benefit data could not be retrieved for this drug."
        />
      )}

      {hasRealTimeData && data.patientSpecificPricingCoverages?.length && (
        <div className="flex flex-col gap-2">
          <BodyText size="sm" className="font-medium">
            Pricing
          </BodyText>
          <PricingSection coverages={data.patientSpecificPricingCoverages} />
        </div>
      )}

      {hasRealTimeData && alternatives && alternatives.length > 0 && (
        <div className="flex flex-col gap-2">
          <BodyText size="sm" className="font-medium">
            Alternatives
          </BodyText>
          <div className="flex flex-col gap-2">
            {alternatives.map((alt) => (
              <div
                key={alt.ndc ?? alt.displayName}
                className="rounded-md border p-3 flex flex-col gap-1"
              >
                <span className="text-sm font-medium">{alt.displayName}</span>
                <PricingSection
                  coverages={alt.patientSpecificPricingCoverages}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface BenefitsResultsProps {
  ndc: string;
  medicationName: string;
  payerName?: string;
  pharmacyId: number;
  quantity: number;
  daysSupply: number;
  dispenseUnitTypeId: number;
  patientEligibilityId?: number;
  onBack: () => void;
}

const BenefitsResults = ({
  ndc,
  medicationName,
  payerName,
  pharmacyId,
  quantity,
  daysSupply,
  dispenseUnitTypeId,
  patientEligibilityId,
  onBack,
}: BenefitsResultsProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { data, isLoading, isError, refetch } = useRxPrescriptionBenefits({
    patientId,
    ndc,
    pharmacyId,
    quantity,
    daysSupply,
    dispenseUnitTypeId,
    patientEligibilityId,
    enabled: true,
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button
          variant="icon"
          className="m-0 p-0"
          onClick={onBack}
          aria-label="Back"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex flex-col">
          <BodyText size="sm" className="font-medium">
            {medicationName}
          </BodyText>
          {payerName && (
            <BodyText size="sm" textColor="tertiary">
              {payerName}
            </BodyText>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton
            <Skeleton key={i} className="h-8" />
          ))}
        </div>
      )}

      {isError && (
        <div className="flex flex-col gap-3">
          <Callout
            variant="danger"
            title="Error loading prescription benefits"
            description="Unable to retrieve benefit information."
          />
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}

      {data && !isLoading && !isError && <BenefitsData data={data} />}
    </div>
  );
};

export const PrescriptionBenefitsModal = ({
  open,
  onOpenChange,
  ndc,
  medicationName,
  pharmacyId,
  quantity,
  daysSupply,
  dispenseUnitTypeId,
  pharmacy,
}: PrescriptionBenefitsModalProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [selectedEligibilityId, setSelectedEligibilityId] =
    useState<string>("");
  const [showResults, setShowResults] = useState(false);

  const { data: eligibilityData, isLoading: isEligibilityLoading } =
    useRxEligibility({ patientId, enabled: open });

  const canCheck = Boolean(
    ndc &&
      pharmacyId &&
      quantity &&
      daysSupply &&
      dispenseUnitTypeId &&
      selectedEligibilityId,
  );

  const handleClose = (value: boolean) => {
    onOpenChange(value);
    if (!value) {
      setShowResults(false);
      setSelectedEligibilityId("");
    }
  };

  if (
    showResults &&
    ndc &&
    pharmacyId &&
    quantity &&
    daysSupply &&
    dispenseUnitTypeId
  ) {
    const eligibilityIdNum = selectedEligibilityId
      ? Number(selectedEligibilityId)
      : undefined;
    const payerName = eligibilityData?.coverages.find(
      (c) => String(c.patientEligibilityId) === selectedEligibilityId,
    )?.payerName;

    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogOverlay />
        <DialogContent className="w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Prescription Benefits</DialogTitle>
            <DialogDescription className="hidden">
              Prescription benefit results
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
            <BenefitsResults
              ndc={ndc}
              medicationName={medicationName}
              payerName={payerName}
              pharmacyId={pharmacyId}
              quantity={quantity}
              daysSupply={daysSupply}
              dispenseUnitTypeId={dispenseUnitTypeId}
              patientEligibilityId={
                !Number.isNaN(eligibilityIdNum) ? eligibilityIdNum : undefined
              }
              onBack={() => setShowResults(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const { city, line, postalCode, state } = pharmacy?.address ?? {};
  const [phone] = pharmacy?.telecom ?? [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogOverlay />
      <DialogContent className="w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Prescription Benefits</DialogTitle>
          <DialogDescription className="hidden">
            Check prescription benefit information
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <BodyText size="sm" className="font-medium">
              Pharmacy
            </BodyText>
            {pharmacy ? (
              <div className="rounded-md border p-3 text-sm flex flex-col gap-0.5">
                <span className="font-medium">{pharmacy.name}</span>
                {line && line.length > 0 && (
                  <span className="text-tertiary">{line.join(", ")}</span>
                )}
                {(city || state || postalCode) && (
                  <span className="text-tertiary">
                    {[city, state, postalCode].filter(Boolean).join(", ")}
                  </span>
                )}
                {phone?.value && (
                  <span className="text-tertiary">
                    {formatPhoneNumber(phone.value)}
                  </span>
                )}
              </div>
            ) : (
              <BodyText size="sm" textColor="tertiary">
                No pharmacy selected
              </BodyText>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <BodyText size="sm" className="font-medium">
              Eligibility
            </BodyText>
            {isEligibilityLoading ? (
              <Skeleton className="h-9" />
            ) : (
              <Select
                value={selectedEligibilityId}
                onValueChange={setSelectedEligibilityId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {eligibilityData?.coverages
                      .filter((c) => c.patientEligibilityId != null)
                      .map((coverage) => (
                        <SelectItem
                          key={coverage.patientEligibilityId}
                          value={String(coverage.patientEligibilityId)}
                        >
                          {coverage.payerName}
                          {coverage.planName ? ` — ${coverage.planName}` : ""}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="pt-1">
          <Button
            className="w-full"
            disabled={!canCheck}
            onClick={() => setShowResults(true)}
          >
            Check Benefits
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
